import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import axios from "axios";
import { PowerBIEmbed } from "powerbi-client-react";
import { Permissions, TokenType } from "powerbi-models";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { PowerBiEmbedConfig } from "@thalamos/vision-common";
import {
  isCurrentUserSupervisor,
  PowerBiEmbedConfigSchema,
} from "@thalamos/vision-common";

import api from "../../api/index.js";
import { Dashboard } from "../../components/Admin/Dashboard/Dashboard.js";
import { DataGlossary } from "../../components/Admin/DataGlossary/DataGlossary.js";
import { IncidentManagement } from "../../components/Admin/Incident/Incident.js";
import { useUser } from "../../Context/UserContext.js";
import { createRoute } from "../../routing/createRoute.js";
import { logErrorToDatadog } from "../../tracing.js";

export default function SupervisorDashboardPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();
  const { user } = useUser();

  const embedDashboardEnabled =
    user?.sessionOrganisationConfiguration?.embedDashboard !== undefined;

  const embedPowerBiDashboardEnabled =
    user?.sessionOrganisationConfiguration?.embedPowerBiDashboard === true;

  const handleChange = (_event: SyntheticEvent, newTab: number) => {
    setTab(newTab);
  };

  useEffect(() => {
    if (!user) return;
    const isSupervisorUser = isCurrentUserSupervisor(user);
    if (!isSupervisorUser) {
      navigate(createRoute.home());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <Box>
      <Tabs
        sx={{ padding: "1em 0" }}
        variant="scrollable"
        scrollButtons="auto"
        value={tab}
        onChange={handleChange}
      >
        <Tab label={t("supervisor.tabs.incidents")} value={0} />
        {embedDashboardEnabled && (
          <Tab label={t("supervisor.tabs.dashboards")} value={1} />
        )}
        {embedDashboardEnabled && (
          <Tab label={t("supervisor.tabs.dataGlossary")} value={2} />
        )}
        {embedPowerBiDashboardEnabled && (
          <Tab label={t("supervisor.tabs.v1Dashboard")} value={3} />
        )}
      </Tabs>
      {tab === 0 && (
        <Box>
          <IncidentManagement />
        </Box>
      )}
      {tab === 1 && embedDashboardEnabled && (
        <Box>
          <Dashboard />
        </Box>
      )}
      {tab === 2 && embedDashboardEnabled && (
        <Box>
          <DataGlossary />
        </Box>
      )}
      {tab === 3 && embedPowerBiDashboardEnabled && (
        <Box>
          <PowerBiDashboardInline />
        </Box>
      )}
    </Box>
  );
}

function PowerBiDashboardInline() {
  const { t } = useTranslation();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<PowerBiEmbedConfig | null>(
    null,
  );

  const organisationId = user?.sessionContext?.organisationId;

  const fetchPowerBiConfig = useCallback(async () => {
    if (!organisationId) {
      throw new Error("No organisation ID available");
    }
    const response = await api.report.getPowerBiDashboard(organisationId);
    const parsed = PowerBiEmbedConfigSchema.safeParse(response.data);
    if (!parsed.success) {
      throw new Error(
        `Invalid Power BI config response: ${parsed.error.message}`,
      );
    }
    return parsed.data;
  }, [organisationId]);

  useEffect(() => {
    if (!organisationId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const config = await fetchPowerBiConfig();
        setDashboardData(config);
      } catch (err) {
        logErrorToDatadog(err instanceof Error ? err : new Error(String(err)));
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError("not-configured");
        } else {
          setError(
            err instanceof Error
              ? err.message
              : t("supervisor.powerBiDashboard.fetchError"),
          );
        }
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [organisationId, t, fetchPowerBiConfig]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error === "not-configured") {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>
          {t("supervisor.powerBiDashboard.notConfigured")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {t("supervisor.powerBiDashboard.errorLoading", { error })}
        </Typography>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t("supervisor.powerBiDashboard.noData")}</Typography>
      </Box>
    );
  }

  return (
    <>
      <style>{`.power-bi-embed-fullsize { height: 95%; width: 100%; }`}</style>
      <Alert severity="info" sx={{ m: 2 }}>
        {t("supervisor.powerBiDashboard.deprecationWarning")}
      </Alert>
      <Box
        sx={{
          height: "80vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ height: "95%", width: "100%" }}>
          <PowerBIEmbed
            cssClassName="power-bi-embed-fullsize"
            embedConfig={{
              type: "report",
              id: dashboardData.dashboardId,
              permissions: Permissions.Read,
              embedUrl: dashboardData.embedUrl,
              accessToken: dashboardData.embedToken,
              tokenType: TokenType.Embed,
              pageView: "fitToWidth",
              settings: {
                panes: {
                  filters: { expanded: true, visible: true },
                  pageNavigation: { visible: true },
                },
                background: 1, // Transparent
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
}
